import groq from 'groq';
import { slugWithParents } from '@pkg/sanity-utils/slugWithParents';
import { DOCUMENT } from '@pkg/config/sanity/schemas';

export interface SolutionInput {
  vendorTypeId: string;
  postalTown?: string;
  locality?: string;
  administrativeAreaLevel2?: string;
  administrativeAreaLevel1?: string;
  country: string;
}

export interface SolutionPageMatch {
  _type: string;
  /** Full path from slugWithParents, including the parent slug. */
  slug: string;
  /** Document slug, shared by the legacy page and its audience-tagged copy. */
  leafSlug: string;
  localities: null | number;
  areas: null | number;
  /** True when this document passes the current site's audience filter. */
  servable: boolean;
}

export interface QueryResponse {
  pages: Array<SolutionPageMatch>;
  backupPages: Array<SolutionPageMatch>;
  /** Audience-visible marketplace.pages pages with the same leaf slug. */
  replacements: Array<{
    _type: string;
    slug: string;
    leafSlug: string;
  }>;
  fallbackPage: null | {
    _type: string;
    slug: string;
  };
}

/**
 * For the given address parts, combine into a query that can search the marketplace solution finder
 * pages, and return the most relevant page.
 */
export function findSolutionPage(solutionInput: SolutionInput, audienceFilter: string) {
  const parts = [];

  if (solutionInput.administrativeAreaLevel1 || solutionInput.administrativeAreaLevel2) {
    const adminAreaParts = [];
    if (solutionInput.administrativeAreaLevel1) {
      const areaLevel1 = solutionInput.administrativeAreaLevel1.toLowerCase();
      adminAreaParts.push(`"${areaLevel1}" in targetAdministrativeAreas[]`);
    }
    if (solutionInput.administrativeAreaLevel2) {
      const areaLevel2 = solutionInput.administrativeAreaLevel2.toLowerCase();
      adminAreaParts.push(`"${areaLevel2}" in targetAdministrativeAreas[]`);
    }

    parts.push(`(count(targetAdministrativeAreas) == 0 || count(targetAdministrativeAreas) == null || ${adminAreaParts.join(' || ')})`);
  } else {
    parts.push('(count(targetAdministrativeAreas) == 0 || count(targetAdministrativeAreas) == null)');
  }

  if (solutionInput.locality || solutionInput.postalTown) {
    const townParts = [];
    if (solutionInput.locality) {
      townParts.push(`"${solutionInput.locality.toLowerCase()}" in targetTownsLocalities[]`);
    }
    if (solutionInput.postalTown) {
      townParts.push(`"${solutionInput.postalTown.toLowerCase()}" in targetTownsLocalities[]`);
    }

    parts.push(`(count(targetTownsLocalities) == 0 || count(targetTownsLocalities) == null || ${townParts.join(' || ')})`);
  } else {
    parts.push('(count(targetTownsLocalities) == 0 || count(targetTownsLocalities) == null)');
  }

  const vendorTypeId = solutionInput.vendorTypeId;
  const country = solutionInput.country.toLowerCase();
  const legacyType = DOCUMENT.BUSINESS_SOLUTIONS_MARKETPLACES;
  const visibleType = DOCUMENT.MARKETPLACE_PAGES;
  const vendorAndCountry = groq`
    _type in ["${legacyType}", "${visibleType}"]
    && language == $locale
    && "${vendorTypeId}" in vendorType[]->_id
    && "${country}" in targetCountries[]
  `;
  const pageFields = groq`
    _type,
    "leafSlug": slug.current,
    ${slugWithParents},
    "localities": count(targetTownsLocalities),
    "areas": count(targetAdministrativeAreas),
    "servable": ${audienceFilter},
  `;

  return groq`{
    "pages": *[
      ${vendorAndCountry}
      ${parts.length ? `&& (${parts.join(' && ')})` : ''}
    ] {
      ${pageFields}
    },
    "backupPages": *[
      ${vendorAndCountry}
      && (count(targetTownsLocalities) == 0 || count(targetTownsLocalities) == null)
      && (count(targetAdministrativeAreas) == 0 || count(targetAdministrativeAreas) == null)
    ] {
      ${pageFields}
    },
    "replacements": *[
      _type == "${visibleType}"
      && language == $locale
      && ${audienceFilter}
      && slug.current in *[${vendorAndCountry}].slug.current
    ] {
      _type,
      "leafSlug": slug.current,
      ${slugWithParents},
    },
    "fallbackPage": *[_id == $fallbackPageId][0] {
      _type,
      ${slugWithParents},
    }
  }`;
}
